import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import type { Profile } from '@/types';
import {
  getProfileByHandle,
  getProfileById,
  updateProfileCosmetic,
  updateProfileFields,
  updateProfileLinks,
  updateProfileVisibility,
  type CosmeticPatch,
  type ProfileFieldsPatch,
  type VisibilityPatch,
} from '../api/profiles';

/** A profile by auth id — used where we have an id but not the cached object. */
export function useProfileById(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.profile(id ?? ''),
    queryFn: () => getProfileById(id!),
    enabled: Boolean(id),
  });
}

/** A public profile by `@handle` — drives the `/u/:handle` route. */
export function useProfileByHandle(handle: string | undefined) {
  return useQuery({
    queryKey: queryKeys.profileByHandle(handle ?? ''),
    queryFn: () => getProfileByHandle(handle!),
    enabled: Boolean(handle),
  });
}

/**
 * Persist a title/emblem change. On success we refresh the cached profile so
 * the page reflects it; the self page additionally calls `refreshProfile()`
 * from AuthProvider so the TopNav avatar/menu update too.
 */
export function useUpdateProfileCosmetic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CosmeticPatch }) =>
      updateProfileCosmetic(id, patch),
    onSuccess: (profile: Profile) => {
      qc.setQueryData(queryKeys.profile(profile.id), profile);
      if (profile.handle) {
        qc.setQueryData(queryKeys.profileByHandle(profile.handle), profile);
      }
    },
  });
}

/** Persist an About-section visibility toggle. Same cache-sync as cosmetics. */
export function useUpdateProfileVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: VisibilityPatch }) =>
      updateProfileVisibility(id, patch),
    onSuccess: (profile: Profile) => {
      qc.setQueryData(queryKeys.profile(profile.id), profile);
      if (profile.handle) {
        qc.setQueryData(queryKeys.profileByHandle(profile.handle), profile);
      }
    },
  });
}

/**
 * Persist the editable public-profile fields (avatar / name / handle / bio /
 * status) from the profile-page editor. Same cache-sync as the cosmetic
 * mutation; the caller refreshes AuthProvider so the TopNav reflects it too.
 */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      fields,
      stampNameChange,
    }: {
      id: string;
      fields: ProfileFieldsPatch;
      stampNameChange?: boolean;
    }) => updateProfileFields(id, fields, stampNameChange),
    onSuccess: (profile: Profile) => {
      qc.setQueryData(queryKeys.profile(profile.id), profile);
      if (profile.handle) {
        qc.setQueryData(queryKeys.profileByHandle(profile.handle), profile);
      }
    },
  });
}

/** Persist the social-links map. Same cache-sync as the other profile writes. */
export function useUpdateProfileLinks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, links }: { id: string; links: Record<string, string> }) =>
      updateProfileLinks(id, links),
    onSuccess: (profile: Profile) => {
      qc.setQueryData(queryKeys.profile(profile.id), profile);
      if (profile.handle) {
        qc.setQueryData(queryKeys.profileByHandle(profile.handle), profile);
      }
    },
  });
}
